import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeletons } from "@/components/common/EmptyState";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, Warning, SealCheck, Star, Fire, CheckSquare, Megaphone, Package, At, Checks } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = { alert: Warning, approval: SealCheck, influencer: Star, milestone: Fire, task: CheckSquare, campaign: Megaphone, inventory: Package, mention: At };
const COLORS = { alert: "#FB7185", approval: "#FACC15", influencer: "#EC4899", milestone: "#A3E635", task: "#3B82F6", campaign: "#7C3AED", inventory: "#2DD4BF", mention: "#3B82F6" };

export default function Notifications() {
  const qc = useQueryClient();
  const { data: items = [], isLoading } = useQuery({ queryKey: ["my-notifications"], queryFn: async () => (await api.get("/notifications")).data });
  const unread = items.filter((n) => !n.read).length;

  const markRead = async (n) => { await api.patch(`/resource/notifications/${n.id}`, { read: true }); qc.invalidateQueries({ queryKey: ["my-notifications"] }); };
  const markAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["my-notifications"] }); toast.success("All marked as read"); };

  return (
    <div>
      <PageHeader title="Notifications" subtitle={unread ? `${unread} unread` : "You're all caught up"} icon={Bell}
        actions={unread > 0 && <Button variant="outline" onClick={markAll} data-testid="mark-all-read" className="rounded-full"><Checks size={16} className="mr-1" /> Mark all read</Button>} />
      {isLoading ? <Skeletons /> : (
        <div className="space-y-2">
          {items.map((n, i) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <div key={n.id} onClick={() => markRead(n)} data-testid="notification-item"
                className={`flex cursor-pointer items-center gap-4 rounded-2xl border border-border p-4 transition-colors hover:bg-secondary/40 ${n.read ? "bg-surface opacity-60" : "bg-surface-raised"}`}
                style={{ animation: `fade-up 0.3s ease-out ${i * 0.03}s both` }}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: `${COLORS[n.type]}22`, color: COLORS[n.type] }}><Icon size={20} weight="duotone" /></div>
                <div className="flex-1">
                  <p className="text-sm">{n.message}</p>
                  {n.created_at && <p className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>}
                </div>
                {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-brand-pink" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
